import { React, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, FormText, Spinner } from "reactstrap";
import Select from "react-select";
import { v4 as uuid } from "uuid";
import { createDigDay } from "../../redux/slices/DigDaysSlice";
import { getUsersInfos } from "../../redux/slices/UserSlice";

const DAY_PERIOD_OPTIONS = [
  { value: 0, label: "Journée entière" },
  { value: 1, label: "Matin (AM)" },
  { value: 2, label: "Après-midi (PM)" },
];

const IS_AT_OFFICE_OPTIONS = [
  { value: true, label: "Oui" },
  { value: false, label: "Non" },
];

const todayIso = () => new Date().toISOString().split("T")[0];

const isWeekend = (dateStr) => {
  if (!dateStr) return false;
  const day = new Date(dateStr).getUTCDay();
  return day === 0 || day === 6;
};

const getCurrentYearBalance = (user) => {
  const currentYear = new Date().getFullYear();
  if (!user.userDigDayBalances || user.userDigDayBalances.length === 0) return null;
  const record = user.userDigDayBalances.find((b) => b.year === currentYear);
  return record ? record.balance : null;
};

const getMinBalanceRequired = (dayPeriodValue) => {
  return dayPeriodValue === 0 ? 1 : 0.5;
};

const buildEmptyForm = () => ({
  userId: null,
  title: "",
  description: "",
  date: todayIso(),
  dayPeriod: DAY_PERIOD_OPTIONS[0],
  isAtOffice: IS_AT_OFFICE_OPTIONS[1],
});

const CreateDigDayModal = ({ activeUsers }) => {
  const dispatch = useDispatch();
  const { loading_createDigDay } = useSelector((state) => ({ ...state.digDays }));

  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(buildEmptyForm());
  const [errors, setErrors] = useState({});

  const toggle = () => {
    if (!modal) {
      setForm(buildEmptyForm());
      setErrors({});
    }
    setModal(!modal);
  };

  const closeBtn = (
    <Button className="close" onClick={toggle} disabled={loading_createDigDay}>
      &times;
    </Button>
  );

  const myBtn = (
    <Button
      color="success"
      onClick={toggle}
      data-bs-toggle="tooltip"
      data-bs-placement="top"
      title="Créer un Dig Day pour un explorateur"
    >
      <i className="bx bx-plus" style={{ fontSize: "1.2em" }}></i>&nbsp;Créer un Dig Day
    </Button>
  );

  const minBalance = getMinBalanceRequired(form.dayPeriod.value);

  const userOptions = useMemo(() => {
    if (!activeUsers || activeUsers.length === 0) return [];
    return [...activeUsers]
      .filter((u) => {
        if (!u.isActivated) return false;
        const balance = getCurrentYearBalance(u);
        if (balance === null) return false;
        return balance >= minBalance;
      })
      .sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      )
      .map((u) => {
        const balance = getCurrentYearBalance(u);
        return {
          value: u.userId,
          label: `${u.firstName} ${u.lastName} — ${balance} jour(s) restant(s)`,
        };
      });
  }, [activeUsers, minBalance]);

  const handlePeriodChange = (choice) => {
    const newMin = getMinBalanceRequired(choice.value);
    if (form.userId) {
      const selectedUser = activeUsers?.find((u) => u.userId === form.userId.value);
      if (selectedUser) {
        const balance = getCurrentYearBalance(selectedUser);
        if (balance === null || balance < newMin) {
          setForm((prev) => ({ ...prev, dayPeriod: choice, userId: null }));
          return;
        }
      }
    }
    setForm((prev) => ({ ...prev, dayPeriod: choice }));
  };

  const validate = () => {
    const e = {};
    if (!form.userId) e.userId = "Veuillez sélectionner un collaborateur.";
    if (!form.title.trim()) e.title = "Le titre est obligatoire.";
    if (!form.date) {
      e.date = "La date est obligatoire.";
    } else if (isWeekend(form.date)) {
      e.date = "La date ne peut pas être un week-end.";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    const d = new Date(form.date);
    const pad = (n) => String(n).padStart(2, "0");
    const dateStr = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T00:00:00Z`;

    const payload = {
      digDayId: uuid(),
      userId: form.userId.value,
      title: form.title.trim(),
      description: form.description.trim() || null,
      date: dateStr,
      dayPeriod: form.dayPeriod.value,
      isAtOffice: form.isAtOffice.value,
      actionIds: [],
    };

    const result = await dispatch(createDigDay(payload));
    if (result?.payload?.status === 200) {
      await dispatch(getUsersInfos());
      setForm(buildEmptyForm());
      setErrors({});
    }
  };

  return (
    <div>
      {myBtn}
      <Modal isOpen={modal} toggle={toggle} backdrop={"static"} keyboard={false} size="md">
        <ModalHeader toggle={toggle} close={closeBtn}>
          Créer un Dig Day
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleSubmit}>

            <FormGroup>
              <Label for="userId">
                Collaborateur <span className="text-danger">*</span>
              </Label>
              <Select
                inputId="userId"
                name="userId"
                placeholder={
                  userOptions.length === 0
                    ? "Aucun collaborateur avec du solde disponible"
                    : "Sélectionner un collaborateur..."
                }
                options={userOptions}
                value={form.userId}
                onChange={(choice) => {
                  setForm((prev) => ({ ...prev, userId: choice }));
                  setErrors((prev) => ({ ...prev, userId: undefined }));
                }}
                noOptionsMessage={() => "Aucun collaborateur avec du solde disponible"}
                isClearable
                isSearchable
                isDisabled={userOptions.length === 0}
              />
              {errors.userId && (
                <div className="text-danger small mt-1">{errors.userId}</div>
              )}
            </FormGroup>

            <FormGroup>
              <Label for="title">
                Titre <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="title"
                name="title"
                placeholder="Liste des compétences travaillées"
                value={form.title}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, title: e.target.value }));
                  setErrors((prev) => ({ ...prev, title: undefined }));
                }}
                invalid={!!errors.title}
                autoComplete="off"
              />
              <FormFeedback>{errors.title}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                rows={3}
                placeholder="Détail des activités réalisées & ressources utilisées"
                value={form.description}
                maxLength={1000}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
              <FormText color="muted">{form.description.length}/1000 caractères</FormText>
            </FormGroup>

            <FormGroup>
              <Label for="date">
                Date <span className="text-danger">*</span>
              </Label>
              <Input
                type="date"
                id="date"
                name="date"
                value={form.date}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, date: e.target.value }));
                  setErrors((prev) => ({ ...prev, date: undefined }));
                }}
                invalid={!!errors.date}
              />
              <FormFeedback>{errors.date}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="dayPeriod">
                Durée <span className="text-danger">*</span>
              </Label>
              <Select
                inputId="dayPeriod"
                name="dayPeriod"
                options={DAY_PERIOD_OPTIONS}
                value={form.dayPeriod}
                onChange={handlePeriodChange}
                noOptionsMessage={() => "Pas d'options"}
              />
              <FormText color="muted">
                La liste des collaborateurs est filtrée selon la durée sélectionnée.
              </FormText>
            </FormGroup>

            <FormGroup>
              <Label for="isAtOffice">
                Au bureau <span className="text-danger">*</span>
              </Label>
              <Select
                inputId="isAtOffice"
                name="isAtOffice"
                options={IS_AT_OFFICE_OPTIONS}
                value={form.isAtOffice}
                onChange={(choice) =>
                  setForm((prev) => ({ ...prev, isAtOffice: choice }))
                }
                noOptionsMessage={() => "Pas d'options"}
              />
            </FormGroup>

          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={toggle} disabled={loading_createDigDay}>
            Annuler
          </Button>
          <Button color="success" onClick={handleSubmit} disabled={loading_createDigDay}>
            {loading_createDigDay ? (
              <>
                <Spinner size="sm" className="me-1" />
                Création...
              </>
            ) : (
              "Créer"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateDigDayModal;
